// Size of the moving storm glyph, from the equivalent radius of the >=60 km/h wind area at the playhead.
// Pure (no Leaflet/React): area in km2 on the sphere, radius linearly interpolated between timed sets.

const EARTH_KM = 6371.0088;
const RAD = Math.PI / 180;
const BASE_PX = 20;
const PX_PER_KM = 0.1;
export const ICON_MIN_PX = 24;
export const ICON_MAX_PX = 48;
// Equivalent radii (km) used where a timed set has no usable polygon: missing means unknown, not zero.
export const CLASS_RADIUS_KM = { HU: 200, TS: 120, TD: 40, UNK: 120 };

const radiusForClass = (cls) => CLASS_RADIUS_KM[cls] ?? CLASS_RADIUS_KM.UNK;

// Unsigned spherical area (km2) of one ring, longitudes unwrapped across the antimeridian.
export function sphericalRingAreaKm2(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const dl = ((((b[0] - a[0]) * RAD + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI) - Math.PI;
    sum += dl * (Math.sin(a[1] * RAD) + Math.sin(b[1] * RAD));
  }
  return (Math.abs(sum) * EARTH_KM * EARTH_KM) / 2;
}

// Prepared geometry -> area (km2): exteriors minus holes. null when there is nothing usable.
export function preparedAreaKm2(prepared) {
  if (!prepared?.polys?.length) return null;
  const area = prepared.polys.reduce(
    (s, p) => s + Math.max(0, sphericalRingAreaKm2(p.outer) - p.holes.reduce((h, r) => h + sphericalRingAreaKm2(r), 0)),
    0,
  );
  return Number.isFinite(area) && area > 0 ? area : null;
}

// Piecewise-linear value at t over [{ t, v }] sorted by t, held at both ends.
function lerpNodes(nodes, t) {
  if (t <= nodes[0].t) return nodes[0].v;
  const last = nodes[nodes.length - 1];
  if (t >= last.t) return last.v;
  let i = 0;
  while (i < nodes.length - 2 && nodes[i + 1].t <= t) i += 1;
  const f = (t - nodes[i].t) / (nodes[i + 1].t - nodes[i].t);
  return nodes[i].v + (nodes[i + 1].v - nodes[i].v) * f;
}

const cache = new WeakMap();

// -> { first, last, nodes: [{ t, v (km) }] } | null, built once per parsed object.
function radiusModel(parsed) {
  if (cache.has(parsed)) return cache.get(parsed);
  const F = (parsed?.track?.vertices || []).filter((v) => v.phase === 'forecast' && v.timeMs != null);
  let model = null;
  if (F.length >= 2) {
    const classNodes = F.map((v) => ({ t: v.timeMs, v: radiusForClass(v.cls) }));
    const timed = (parsed.windByTime || []).map((w) => {
      const measured = preparedAreaKm2(w.sets?.[60]);
      return { t: w.timeMs, v: measured != null ? Math.sqrt(measured / Math.PI) : lerpNodes(classNodes, w.timeMs) };
    });
    model = { first: F[0].timeMs, last: F[F.length - 1].timeMs, nodes: timed.length ? timed : classNodes };
  }
  cache.set(parsed, model);
  return model;
}

/** Glyph size in px (24..48) at clockMs, or null when no moving icon exists (ended event / not animatable). */
export function iconSizePx(parsed, clockMs, { isCurrent = true } = {}) {
  if (!parsed || !isCurrent || !parsed.track?.animatable || !Number.isFinite(clockMs)) return null;
  const m = radiusModel(parsed);
  if (!m) return null;
  const t = Math.min(m.last, Math.max(m.first, clockMs));
  const r = lerpNodes(m.nodes, t);
  return Math.max(ICON_MIN_PX, Math.min(ICON_MAX_PX, BASE_PX + PX_PER_KM * r));
}

export const quantisePx = (px) => Math.round(px * 4) / 4;
